import React, { useState, useMemo } from "react";
import { Calendar, AlertTriangle, CheckCircle2, ArrowRight, Info } from "lucide-react";
import { Link } from "react-router-dom";
import ContactSection from "./ContactSection";

const PREFILLED_OGGETTO = "Impugnazione licenziamento — consulenza urgente";
const PREFILLED_MESSAGGIO =
    "Buongiorno, ho verificato i termini per impugnare il mio licenziamento e vorrei una consulenza urgente.";

// Formatta una data JS in "DD MMMM YYYY" italiano
function formatItalianDate(date) {
    if (!date || isNaN(date.getTime())) return "";
    return date.toLocaleDateString("it-IT", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

// Aggiunge giorni di calendario a una data
function addDays(date, days) {
    const d = new Date(date.getTime());
    d.setDate(d.getDate() + days);
    return d;
}

// Giorni rimanenti da oggi (0 se scaduta, negativi = giorni fa)
function daysFromToday(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date.getTime());
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
    return diff;
}

export default function DismissalDeadlineCalculator() {
    const [notifyDate, setNotifyDate] = useState("");
    const [challengeDate, setChallengeDate] = useState("");

    const result = useMemo(() => {
        if (!notifyDate) return null;
        const nDate = new Date(notifyDate);
        if (isNaN(nDate.getTime())) return null;

        // Scadenza impugnazione stragiudiziale: 60 giorni dalla notifica
        const deadlineChallenge = addDays(nDate, 60);
        const challengeDays = daysFromToday(deadlineChallenge);

        // Scadenza deposito ricorso: 180 giorni dall'impugnazione stragiudiziale
        // Se non fornita la data di impugnazione, assumiamo l'ultimo giorno utile (60 gg)
        let effectiveChallenge = deadlineChallenge;
        let usedProvidedChallenge = false;
        if (challengeDate) {
            const cDate = new Date(challengeDate);
            if (!isNaN(cDate.getTime())) {
                effectiveChallenge = cDate;
                usedProvidedChallenge = true;
            }
        }
        const deadlineDeposit = addDays(effectiveChallenge, 180);
        const depositDays = daysFromToday(deadlineDeposit);

        return {
            deadlineChallenge,
            challengeDays,
            deadlineDeposit,
            depositDays,
            usedProvidedChallenge,
        };
    }, [notifyDate, challengeDate]);

    const statusFor = (days) => {
        if (days < 0) return { label: "TERMINE SCADUTO", cls: "text-red-700 bg-red-50 border-red-300", Icon: AlertTriangle };
        if (days <= 15) return { label: `Restano ${days} giorni — urgente`, cls: "text-amber-800 bg-amber-50 border-amber-300", Icon: AlertTriangle };
        return { label: `Restano ${days} giorni`, cls: "text-emerald-800 bg-emerald-50 border-emerald-300", Icon: CheckCircle2 };
    };

    return (
        <section
            id="calcola-scadenze"
            data-testid="dismissal-calculator-section"
            className="py-24 lg:py-32 bg-navy relative overflow-hidden"
        >
            {/* decorative gold vertical line */}
            <div className="absolute top-0 bottom-0 left-10 lg:left-24 w-px bg-gold/25 hidden md:block" />

            <div className="max-w-6xl mx-auto px-6 lg:px-10 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* LEFT: intro */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="block w-16 h-[2px] bg-gold" />
                            <span className="label-eyebrow !text-gold">Strumento gratuito</span>
                        </div>
                        <h2 className="font-serif text-white text-4xl md:text-5xl leading-[1.05] mb-6">
                            Calcola i <span className="italic text-gold">termini</span> per impugnare il licenziamento
                        </h2>
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
                            Inserisci la data di ricezione del licenziamento e verifica i termini di legge per la contestazione stragiudiziale
                            (art. 6, L. 604/1966) e per il deposito del ricorso in Tribunale.
                        </p>

                        <div className="border-l-2 border-gold/60 pl-5 py-1 text-slate-400 text-sm leading-relaxed">
                            <p className="mb-2">
                                <strong className="text-white">60 giorni</strong> per l&apos;impugnazione stragiudiziale
                                (raccomandata A/R o PEC).
                            </p>
                            <p>
                                <strong className="text-white">180 giorni</strong> dall&apos;impugnazione per il deposito del ricorso
                                o l&apos;attivazione di conciliazione/arbitrato.
                            </p>
                        </div>

                        <div className="mt-8 flex items-start gap-3 text-xs text-slate-400 leading-relaxed">
                            <Info size={14} className="text-gold mt-0.5 flex-shrink-0" />
                            <span>
                                Lo strumento fornisce un calcolo orientativo. Per una valutazione vincolante rivolgiti allo Studio.
                            </span>
                        </div>
                    </div>

                    {/* RIGHT: form + result */}
                    <div className="lg:col-span-7">
                        <div className="bg-white shadow-2xl relative">
                            <div className="absolute -top-px left-0 w-32 h-[2px] bg-gold" />

                            <div className="p-8 lg:p-10">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <label htmlFor="notifyDate" className="block text-xs font-bold tracking-[0.2em] text-navy uppercase mb-3">
                                            Data notifica del licenziamento *
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="notifyDate"
                                                data-testid="calc-notify-date"
                                                type="date"
                                                value={notifyDate}
                                                onChange={(e) => setNotifyDate(e.target.value)}
                                                max={new Date().toISOString().split("T")[0]}
                                                className="w-full border border-slate-300 px-4 py-3 pr-10 text-navy focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors font-medium"
                                            />
                                            <Calendar size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="challengeDate" className="block text-xs font-bold tracking-[0.2em] text-navy uppercase mb-3">
                                            Data di impugnazione stragiudiziale <span className="normal-case font-normal tracking-normal text-slate-500">(opz.)</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="challengeDate"
                                                data-testid="calc-challenge-date"
                                                type="date"
                                                value={challengeDate}
                                                onChange={(e) => setChallengeDate(e.target.value)}
                                                min={notifyDate || undefined}
                                                className="w-full border border-slate-300 px-4 py-3 pr-10 text-navy focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors font-medium disabled:bg-slate-50 disabled:text-slate-400"
                                                disabled={!notifyDate}
                                            />
                                            <Calendar size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>

                                {!result && (
                                    <div data-testid="calc-empty-state" className="border border-dashed border-slate-300 py-10 px-6 text-center">
                                        <p className="text-slate-500 text-sm">
                                            Inserisci la data di ricezione per visualizzare le scadenze.
                                        </p>
                                    </div>
                                )}

                                {result && (
                                    <div data-testid="calc-result" className="space-y-5">
                                        {/* Scadenza 1 — impugnazione */}
                                        <div className="border border-slate-200 p-5 md:p-6">
                                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                                <div>
                                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
                                                        1 · Impugnazione stragiudiziale (60 giorni)
                                                    </div>
                                                    <div className="font-serif text-navy text-2xl md:text-3xl leading-tight capitalize" data-testid="calc-deadline-challenge">
                                                        {formatItalianDate(result.deadlineChallenge)}
                                                    </div>
                                                </div>
                                                <StatusBadge {...statusFor(result.challengeDays)} />
                                            </div>
                                        </div>

                                        {/* Scadenza 2 — deposito ricorso */}
                                        <div className="border border-slate-200 p-5 md:p-6">
                                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                                <div>
                                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
                                                        2 · Deposito ricorso / conciliazione (180 giorni)
                                                    </div>
                                                    <div className="font-serif text-navy text-2xl md:text-3xl leading-tight capitalize" data-testid="calc-deadline-deposit">
                                                        {formatItalianDate(result.deadlineDeposit)}
                                                    </div>
                                                    <div className="text-xs text-slate-500 mt-2">
                                                        {result.usedProvidedChallenge
                                                            ? "Calcolato dalla data di impugnazione inserita."
                                                            : "Calcolato dall'ultimo giorno utile di impugnazione (60° gg)."}
                                                    </div>
                                                </div>
                                                <StatusBadge {...statusFor(result.depositDays)} />
                                            </div>
                                        </div>

                                        {/* CTA */}
                                        <div className="pt-3">
                                            <Link
                                                to="/#contatti"
                                                data-testid="calc-cta"
                                                className="btn-gold w-full sm:w-auto justify-center inline-flex items-center gap-2 group"
                                            >
                                                Richiedi assistenza legale
                                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* INLINE CONTACT FORM (pre-compilato per follow-up sulle scadenze) */}
                {result && (
                    <div className="mt-16 pt-12 border-t border-white/15" data-testid="calc-inline-contact">
                        <div className="max-w-3xl mb-8">
                            <div className="flex items-center gap-4 mb-4">
                                <span className="block w-12 h-[2px] bg-gold" />
                                <span className="label-eyebrow !text-gold">Richiedi consulenza urgente</span>
                            </div>
                            <h3 className="font-serif text-white text-3xl md:text-4xl leading-tight mb-3">
                                Termini stretti? <span className="italic text-gold">Agisci subito</span>.
                            </h3>
                            <p className="text-slate-300 leading-relaxed">
                                I termini di 60 e 180 giorni sono <strong className="text-white">perentori</strong>. Se il tuo caso
                                si avvicina alla scadenza, contattaci ora per una valutazione immediata — la prima consulenza è
                                gratuita.
                            </p>
                        </div>
                        <ContactSection
                            compact
                            sectionId="scadenze-contatto"
                            testId="calc-contact-form-section"
                            heading="Richiesta consulenza urgente"
                            defaultOggetto={PREFILLED_OGGETTO}
                            defaultMessaggio={PREFILLED_MESSAGGIO}
                        />
                    </div>
                )}
            </div>
        </section>
    );
}

function StatusBadge({ label, cls, Icon }) {
    return (
        <div
            data-testid="calc-status-badge"
            className={`inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${cls}`}
        >
            <Icon size={13} />
            {label}
        </div>
    );
}
